import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  AlertTriangle, 
  Mail, 
  CheckCircle, 
  XCircle,
  ArrowRight,
  FileText,
  BookOpen,
  Lightbulb,
  HardHat,
  Shovel,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const safetyTips = [
  {
    icon: Mail,
    title: 'Contact USG First',
    description: 'Contact our team at least 2-3 business days before any digging project. Professional utility locating prevents accidents and saves lives.'
  },
  {
    icon: Eye,
    title: 'Wait for Marks',
    description: 'Do not begin excavation until all utilities have been marked. Marks are typically valid for 14-30 days depending on your state.'
  },
  {
    icon: Shovel,
    title: 'Respect the Zone',
    description: 'Hand dig within 18-24 inches of marked utilities. Never use mechanical equipment in the tolerance zone.'
  },
  {
    icon: HardHat,
    title: 'Protect Exposed Lines',
    description: 'Support and protect any exposed utilities during construction. Backfill properly when work is complete.'
  },
];

const dosDonts = {
  dos: [
    'Always request a locate before you dig',
    'Wait for utility marks before excavating',
    'Hand dig near marked utilities',
    'Use white paint to mark your dig site',
    'Take photos of utility marks',
    'Keep utility marks visible during project',
    'Report any utility damage immediately',
    'Verify marks with your locate technician'
  ],
  donts: [
    'Never assume you know where utilities are',
    'Don\'t ignore expired or faded marks',
    'Never move utility markers',
    'Don\'t use mechanical equipment near marks',
    'Never backfill damaged utilities',
    'Don\'t start digging without a valid ticket',
    'Never dig deeper than your ticket allows',
    'Don\'t ignore "No Conflict" notices'
  ]
};

const colorCodes = [
  { color: 'bg-red-500', name: 'Red', utility: 'Electric Power Lines, Cables, Conduit, Lighting' },
  { color: 'bg-yellow-400', name: 'Yellow', utility: 'Gas, Oil, Steam, Petroleum, Gaseous Materials' },
  { color: 'bg-orange-500', name: 'Orange', utility: 'Communication, Alarm, Signal Lines, Cables' },
  { color: 'bg-blue-500', name: 'Blue', utility: 'Potable Water' },
  { color: 'bg-green-500', name: 'Green', utility: 'Sewer and Drain Lines' },
  { color: 'bg-purple-500', name: 'Purple', utility: 'Reclaimed Water, Irrigation, Slurry Lines' },
  { color: 'bg-pink-400', name: 'Pink', utility: 'Temporary Survey Markings' },
  { color: 'bg-white border border-gray-400', name: 'White', utility: 'Proposed Excavation Area' },
];

const faqs = [
  {
    question: 'Why is professional utility locating important?',
    answer: 'Professional utility locating connects you with experts who can identify and mark underground lines on your property. This service helps utility companies mark their underground lines, preventing costly and dangerous utility strikes during excavation projects.'
  },
  {
    question: 'How far in advance should I request a locate?',
    answer: 'You should request a locate at least 2-3 business days before your planned excavation. Some states require more notice, especially for larger projects. Emergency excavations have different procedures - contact USG directly for guidance.'
  },
  {
    question: 'What if I hit an underground utility?',
    answer: 'If you strike a utility line, stop work immediately. For gas leaks, evacuate the area and call 911. For electrical lines, stay away and call 911. Report all utility damage to the utility company, even if it appears minor.'
  },
  {
    question: 'Do I need a locate for small projects?',
    answer: 'Yes! There\'s no minimum depth or project size exemption. Even installing a mailbox post, planting a tree, or building a fence requires a locate request. Underground utilities can be much shallower than you think, sometimes just a few inches below the surface.'
  },
  {
    question: 'What does "private locate" mean?',
    answer: 'Private locates identify underground utilities that are not registered with public databases, such as on-site sewer lines, private electrical runs, irrigation systems, and other utilities owned by the property owner. These require a separate service from companies like USG.'
  },
  {
    question: 'How long are utility marks valid?',
    answer: 'Utility marks are typically valid for 14-30 days, depending on your state\'s regulations. If your project extends beyond this period or if the marks fade, you\'ll need to request a re-mark before continuing excavation.'
  },
];

export default function SafetyCenterPage() {
  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="safety-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center px-4 py-2 bg-skytec-secondary-dim rounded-full mb-6">
              <Shield className="w-5 h-5 text-skytec-secondary mr-2" />
              <span className="text-skytec-secondary font-mono text-sm">DIG SAFE. WORK SAFE.</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              <span className="neon-text">SAFETY</span> CENTER
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg">
              Underground utility strikes cause injuries, service disruptions, and costly damage every day. 
              Learn how to protect yourself, your crew, and your community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Safety Banner */}
      <section className="relative" data-testid="safety-banner">
        <div className="safety-stripe h-2" />
        <div className="bg-skytec-surface py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-skytec-secondary rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-skytec-fg uppercase">
                    KNOW WHAT'S BELOW. CONTACT BEFORE YOU DIG.
                  </h2>
                  <p className="text-skytec-fg/60 mt-2">
                    It's safe. It's smart. Contact USG for professional utility locating before excavation.
                  </p>
                </div>
              </div>
              <a href="mailto:info@usgllc811.com">
                <Button 
                  size="lg"
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold text-xl px-10"
                  data-testid="email-usg-btn"
                >
                  <Mail className="w-6 h-6 mr-2" />
                  EMAIL USG
                </Button>
              </a>
            </div>
          </div>
        </div>
        <div className="safety-stripe h-2" />
      </section>

      {/* Safety Tips */}
      <section className="py-24" data-testid="safety-tips-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Essential Tips</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              SAFE DIGGING BEST PRACTICES
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {safetyTips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-skytec-surface rounded-xl border border-skytec-border hover:border-skytec-primary/30 transition-colors"
              >
                <div className="w-12 h-12 bg-skytec-primary-dim rounded-lg flex items-center justify-center mb-4">
                  <tip.icon className="w-6 h-6 text-skytec-primary" />
                </div>
                <h3 className="font-heading font-bold text-lg text-skytec-fg mb-2">{tip.title}</h3>
                <p className="text-skytec-fg/60 text-sm">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dos and Don'ts */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="dos-donts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Guidelines</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              DO'S AND DON'TS OF EXCAVATION
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Do's */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-skytec-bg rounded-2xl border border-skytec-border"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-skytec-accent/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-skytec-accent" />
                </div>
                <h3 className="font-heading text-xl font-bold text-skytec-accent uppercase">Do</h3>
              </div>
              <ul className="space-y-3">
                {dosDonts.dos.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-skytec-accent mr-3 mt-1 flex-shrink-0" />
                    <span className="text-skytec-fg/80">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Don'ts */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-skytec-bg rounded-2xl border border-skytec-border"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-skytec-danger/20 rounded-full flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-skytec-danger" />
                </div>
                <h3 className="font-heading text-xl font-bold text-skytec-danger uppercase">Don't</h3>
              </div>
              <ul className="space-y-3">
                {dosDonts.donts.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <XCircle className="w-4 h-4 text-skytec-danger mr-3 mt-1 flex-shrink-0" />
                    <span className="text-skytec-fg/80">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Color Codes */}
      <section className="py-24" data-testid="color-codes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">APWA Standards</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              UTILITY COLOR CODES
            </h2>
            <p className="text-skytec-fg/60 mt-4 max-w-2xl mx-auto">
              The American Public Works Association (APWA) Uniform Color Code identifies underground utilities by color.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {colorCodes.map((code, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="p-4 bg-skytec-surface rounded-xl border border-skytec-border"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 ${code.color} rounded-lg flex-shrink-0`} />
                  <div>
                    <div className="font-heading font-bold text-skytec-fg">{code.name}</div>
                    <div className="text-skytec-fg/60 text-sm">{code.utility}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="safety-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">FAQ</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              FREQUENTLY ASKED QUESTIONS
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-skytec-bg border border-skytec-border rounded-xl overflow-hidden"
              >
                <AccordionTrigger 
                  className="px-6 py-4 text-left font-heading font-semibold text-skytec-fg hover:text-skytec-primary transition-colors [&[data-state=open]]:text-skytec-primary"
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-skytec-fg/70">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" data-testid="safety-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              NEED PROFESSIONAL UTILITY LOCATING?
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Don't risk hitting unmarked utilities. Our certified technicians use advanced 
              technology to locate all underground infrastructure on your site.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-skytec-primary text-skytec-bg hover:bg-skytec-primary/90 font-heading font-bold"
                  data-testid="safety-contact-btn"
                >
                  REQUEST A LOCATE
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-skytec-fg/30 text-skytec-fg hover:bg-skytec-fg/10 font-heading"
                >
                  VIEW OUR SERVICES
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
